import React, { useState, useEffect } from 'react';
import { get } from '../../helpers/apiHelper';
import Swal from 'sweetalert2';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaUsers,
  FaChartLine,
  FaMoneyBillWave,
  FaRetweet,
} from 'react-icons/fa';
import { Tooltip } from 'react-tooltip';
import StatCard from '../../components/statistic/StatCard';
import useAuthStore from '../../store/authStore';

const StoreStatisticsPage = () => {
  const { tiendaId, marcaId, franquiciadoId } = useParams();
  const { franquiciado } = useAuthStore();
  const [selectedTiendaId, setSelectedTiendaId] = useState('');
  const [tiendas, setTiendas] = useState([]);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTiendas = async () => {
      try {
        let response;

        // Si es franquiciado, obtener solo sus tiendas asignadas
        if (franquiciadoId || franquiciado) {
          const franquiciadoIdToUse = franquiciadoId || franquiciado?.franquiciadoId;
          if (franquiciadoIdToUse) {
            response = await get(`franquiciados/${franquiciadoIdToUse}/tiendas`);
          } else {
            response = [];
          }
        } else {
          // Para usuarios normales, obtener todas las tiendas
          response = await get('tiendas');
        }

        // Filtrar tiendas por marcaId si se proporciona
        const tiendasFiltradas = marcaId
          ? response.filter(tienda => tienda.marca === marcaId)
          : response;

        setTiendas(tiendasFiltradas);

        if (tiendasFiltradas.length > 0) {
          const tiendaIdToUse = tiendaId || tiendasFiltradas[0]._id;
          setSelectedTiendaId(tiendaIdToUse);

          // Si no hay tiendaId en la URL, redirigir a la primera tienda
          if (!tiendaId) {
            if (franquiciadoId || franquiciado) {
              const franquiciadoIdToUse = franquiciadoId || franquiciado?.franquiciadoId;
              navigate(`/franquiciado/${franquiciadoIdToUse}/estadisticas/${tiendaIdToUse}`);
            } else {
              navigate(`/store-statistics/tienda/${tiendaIdToUse}`);
            }
          }
        } else {
          setError('No hay tiendas disponibles.');
        }
      } catch (error) {
        console.error('Error fetching tiendas:', error);
        Swal.fire({
          title: 'Error',
          text: 'Error al obtener las tiendas. ' + error.message,
          icon: 'error',
          confirmButtonText: 'Aceptar'
        });
      }
    };

    fetchTiendas();
  }, [tiendaId, marcaId, franquiciadoId, franquiciado, navigate]);

  useEffect(() => {
    if (selectedTiendaId) {
      const fetchStats = async () => {
        try {
          setLoading(true);
          setError(null);

          // Intentar obtener estadísticas específicas de la tienda
          let response;
          try {
            response = await get(`store-statistics/tienda/${selectedTiendaId}`);
          } catch (storeError) {
            console.warn('Store statistics endpoint not available, calculating from tickets:', storeError);
            // Si no existe el endpoint específico, calcular desde los tickets
            response = await calculateStoreStatsFromTickets(selectedTiendaId);
          }
          setStats(response);
        } catch (error) {
          setError('No hay suficientes datos para mostrar estadísticas.');
          console.error('Error fetching statistics:', error);
          Swal.fire({
            title: 'Error',
            text: error.message || 'No hay suficientes datos para mostrar estadísticas.',
            icon: 'error',
            confirmButtonText: 'Aceptar'
          });
        } finally {
          setLoading(false);
        }
      };

      fetchStats();
    }
  }, [selectedTiendaId]);

  // Función para calcular estadísticas de tienda desde los tickets
  const calculateStoreStatsFromTickets = async (tiendaId) => {
    try {
      // Obtener información de la tienda para obtener la marca
      const tiendaResponse = await get(`tiendas/${tiendaId}`);
      const tienda = tiendaResponse;

      if (!tienda) {
        throw new Error('Tienda no encontrada');
      }

      // Obtener todos los tickets de la marca (no solo de la tienda)
      const ticketsResponse = await get(`tickets/marca/${tienda.marca}`);
      const tickets = ticketsResponse || [];

      // Calcular estadísticas usando historialConsumiciones.tienda (CORRECTO)
      let totalVentas = 0;
      let totalTransacciones = 0;
      let totalPuntosAcumulados = 0;
      let totalPuntosCanjeados = 0;
      const clientesUnicos = new Set();
      const fechasVisitas = [];

      tickets.forEach(ticket => {
        if (ticket.historialConsumiciones) {
          ticket.historialConsumiciones.forEach(consumicion => {
            // ✅ CORRECTO: Filtrar por historialConsumiciones.tienda
            if (consumicion.tienda && consumicion.tienda.toString() === tiendaId) {
              totalVentas += consumicion.monto || 0;
              totalTransacciones += 1;
              totalPuntosAcumulados += consumicion.puntos || 0;
              clientesUnicos.add(ticket.cliente.toString());
              fechasVisitas.push(consumicion.fecha);
            }
          });
        }

        // Calcular puntos canjeados desde historialCanjeos
        if (ticket.historialCanjeos) {
          ticket.historialCanjeos.forEach(canje => {
            if (canje.tienda && canje.tienda.toString() === tiendaId) {
              totalPuntosCanjeados += canje.puntos || 0;
            }
          });
        }
      });

      // Obtener total de clientes de la marca para calcular tasa de conversión
      let totalClientesMarca = 0;
      try {
        const clientesMarcaResponse = await get(`marcas/${tienda.marca}/clientes`);
        totalClientesMarca = clientesMarcaResponse ? clientesMarcaResponse.length : 0;
      } catch (error) {
        console.warn('Could not fetch marca clients:', error);
        // Fallback: usar clientes únicos encontrados
        totalClientesMarca = clientesUnicos.size;
      }

      // Calcular métricas
      const totalClientes = clientesUnicos.size;
      const averageTransactionValue = totalTransacciones > 0 ? totalVentas / totalTransacciones : 0;
      const conversionRate = totalClientesMarca > 0 ? (totalClientes / totalClientesMarca) * 100 : 0;
      const redemptionRate = totalPuntosAcumulados > 0 ? (totalPuntosCanjeados / totalPuntosAcumulados) * 100 : 0;

      // Calcular frecuencia de visitas
      const visitFrequency = calculateVisitFrequency(fechasVisitas);

      // Calcular productividad de trabajadores (simplificado)
      const workerProductivity = totalTransacciones > 0 ? totalVentas / totalTransacciones : 0;

      return {
        totalClientes,
        totalVentas: Math.round(totalVentas * 100) / 100,
        averageTransactionValue: Math.round(averageTransactionValue * 100) / 100,
        conversionRate: Math.round(conversionRate * 100) / 100,
        redemptionRate: Math.round(redemptionRate * 100) / 100,
        totalTransacciones,
        totalPuntosAcumulados,
        totalPuntosCanjeados,
        visitFrequency,
        workerProductivity: Math.round(workerProductivity * 100) / 100,
        pointsDistribution: {
          totalPuntosAcumulados,
          totalPuntosRedimidos: totalPuntosCanjeados
        }
      };
    } catch (error) {
      console.error('Error calculating store stats from tickets:', error);
      throw error;
    }
  };

  // Función auxiliar para calcular frecuencia de visitas
  const calculateVisitFrequency = (fechas) => {
    if (fechas.length === 0) {
      return { daily: 0, weekly: 0, monthly: 0 };
    }

    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const visitsToday = fechas.filter(fecha => new Date(fecha) >= oneDayAgo).length;
    const visitsThisWeek = fechas.filter(fecha => new Date(fecha) >= oneWeekAgo).length;
    const visitsThisMonth = fechas.filter(fecha => new Date(fecha) >= oneMonthAgo).length;

    return {
      daily: Math.round(visitsToday * 100) / 100,
      weekly: Math.round(visitsThisWeek * 100) / 100,
      monthly: Math.round(visitsThisMonth * 100) / 100
    };
  };

  const handleTiendaChange = (e) => {
    const newTiendaId = e.target.value;
    setSelectedTiendaId(newTiendaId);

    // Navegar según el contexto (franquiciado o usuario normal)
    if (franquiciadoId || franquiciado) {
      const franquiciadoIdToUse = franquiciadoId || franquiciado?.franquiciadoId;
      navigate(`/franquiciado/${franquiciadoIdToUse}/estadisticas/${newTiendaId}`);
    } else {
      navigate(`/statistics/store/tienda/${newTiendaId}`);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 p-4 md:p-6">
        <div className="container mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <h2 className="text-xl font-semibold text-red-800 mb-2">Error al cargar estadísticas</h2>
            <p className="text-red-600">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-4 md:p-6">
        <div className="container mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando estadísticas de la tienda...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!stats || !tiendas.length) {
    return (
      <div className="min-h-screen bg-gray-100 p-4 md:p-6">
        <div className="container mx-auto">
          <div className="text-center py-12">
            <p className="text-gray-600">Cargando estadísticas...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-6">
      <div className="container mx-auto">
        <h1 className="text-2xl md:text-4xl font-bold mb-4 md:mb-6 text-center text-gray-800">
          Estadísticas de Tienda
        </h1>

        {/* Selector de Tienda */}
        <div className="mb-6">
          <label
            htmlFor="tienda-selector"
            className="block text-lg font-medium text-gray-700"
          >
            Selecciona una Tienda:
          </label>
          <select
            id="tienda-selector"
            value={selectedTiendaId}
            onChange={handleTiendaChange}
            className="mt-2 block w-full md:w-1/2 lg:w-1/3 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
          >
            {tiendas.map((tienda) => (
              <option key={tienda._id} value={tienda._id}>
                {tienda.nombre}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          <StatCard
            icon={<FaUsers />}
            title="Clientes"
            value={stats.totalClientes || 0}
            tooltip="Número total de clientes registrados para esta tienda."
          />
          <StatCard
            icon={<FaChartLine />}
            title="Ventas Totales"
            value={stats.totalVentas ? `€${stats.totalVentas.toFixed(2)}` : '€0.00'}
            tooltip="Total de ventas realizadas en esta tienda."
          />
          <StatCard
            icon={<FaRetweet />}
            title="Tasa de Conversión"
            value={stats.conversionRate ? `${stats.conversionRate.toFixed(1)}%` : '0%'}
            tooltip="Porcentaje de clientes que realizan al menos una compra."
          />
          <StatCard
            icon={<FaMoneyBillWave />}
            title="Valor Promedio de Transacción"
            value={stats.averageTransactionValue ? `€${stats.averageTransactionValue.toFixed(2)}` : '€0.00'}
            tooltip="Promedio del valor de cada transacción realizada en la tienda."
          />
          <StatCard
            icon={<FaRetweet />}
            title="Tasa de Redención de Puntos"
            value={stats.redemptionRate ? `${stats.redemptionRate.toFixed(1)}%` : '0%'}
            tooltip="Porcentaje de puntos acumulados que han sido canjeados por los clientes en esta tienda."
          />
          <StatCard
            icon={<FaChartLine />}
            title="Total Transacciones"
            value={stats.totalTransacciones || 0}
            tooltip="Número total de transacciones realizadas en esta tienda."
          />
        </div>

        {/* Información adicional si hay datos */}
        {(stats.totalPuntosAcumulados > 0 || stats.totalPuntosCanjeados > 0 || stats.visitFrequency) && (
          <div className="mt-8 space-y-6">
            {/* Información de Puntos */}
            {(stats.totalPuntosAcumulados > 0 || stats.totalPuntosCanjeados > 0) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-4">Información de Puntos</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-blue-100">
                    <h4 className="font-medium text-blue-800">Puntos Acumulados</h4>
                    <p className="text-2xl font-bold text-blue-600">{stats.totalPuntosAcumulados || 0}</p>
                    <p className="text-sm text-blue-600">Total de puntos otorgados</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-blue-100">
                    <h4 className="font-medium text-blue-800">Puntos Canjeados</h4>
                    <p className="text-2xl font-bold text-blue-600">{stats.totalPuntosCanjeados || 0}</p>
                    <p className="text-sm text-blue-600">Total de puntos redimidos</p>
                  </div>
                </div>
              </div>
            )}

            {/* Frecuencia de Visitas */}
            {stats.visitFrequency && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-green-900 mb-4">Frecuencia de Visitas</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-green-100">
                    <h4 className="font-medium text-green-800">Diaria</h4>
                    <p className="text-2xl font-bold text-green-600">{stats.visitFrequency.daily || 0}</p>
                    <p className="text-sm text-green-600">Visitas en las últimas 24h</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-green-100">
                    <h4 className="font-medium text-green-800">Semanal</h4>
                    <p className="text-2xl font-bold text-green-600">{stats.visitFrequency.weekly || 0}</p>
                    <p className="text-sm text-green-600">Visitas en los últimos 7 días</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-green-100">
                    <h4 className="font-medium text-green-800">Mensual</h4>
                    <p className="text-2xl font-bold text-green-600">{stats.visitFrequency.monthly || 0}</p>
                    <p className="text-sm text-green-600">Visitas en los últimos 30 días</p>
                  </div>
                </div>
              </div>
            )}

            {/* Productividad de Trabajadores */}
            {stats.workerProductivity > 0 && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-purple-900 mb-4">Productividad</h3>
                <div className="bg-white p-4 rounded-lg border border-purple-100">
                  <h4 className="font-medium text-purple-800">Productividad de Trabajadores</h4>
                  <p className="text-2xl font-bold text-purple-600">€{stats.workerProductivity || 0}</p>
                  <p className="text-sm text-purple-600">Promedio de ventas por transacción</p>
                </div>
              </div>
            )}
          </div>
        )}
        <Tooltip id="tooltip" />
      </div>
    </div>
  );
};

export default StoreStatisticsPage;